import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button, Drawer, Input, Select, Textarea, toast } from '@/components/ui'
import { ProductPicker, type PickerValue } from '@/components/ui/ProductPicker'
import {
  fetchContactDetail,
  useCreateSampleOrder,
  useSampleOrderReps,
  type Party,
} from '../api/queries'
import type { CreateLineInput, PipelineType } from '../api/types'
import { PIPELINE_TYPE_LABELS, PIPELINE_TYPES } from '../utils/stage-meta'
import { CustomerContactPicker } from './CustomerContactPicker'

interface DraftLine extends CreateLineInput {
  _key: number
  _picked: PickerValue
}

export interface NewSampleOrderDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (orderId: string) => void
}

let _lineKeySeq = 1

function emptyLine(): DraftLine {
  return { _key: _lineKeySeq++, _picked: null, final_qty: 1, uom: 'ea' }
}

/**
 * SPI's own commercial-sample intake form. Mirrors the Uviva
 * NewSampleRequestDrawer's Drawer-over-picker-then-navigate shape, with the
 * one deliberate difference the plan called for: line items use the
 * debounced, server-searched ProductPicker (scoped to SPI's own sample-only
 * SKUs) instead of a native `<select>` that loads every product up front —
 * that dropdown-of-hundreds was the exact problem this module exists to fix.
 */
export function NewSampleOrderDrawer({
  open,
  onOpenChange,
  onCreated,
}: NewSampleOrderDrawerProps) {
  const create = useCreateSampleOrder()
  const repsQuery = useSampleOrderReps()
  const reps = repsQuery.data?.reps ?? []

  const [customer, setCustomer] = useState<PickerValue>(null)
  const [prospectName, setProspectName] = useState('')
  const [prospectCompany, setProspectCompany] = useState('')
  const [prospectEmail, setProspectEmail] = useState('')
  const [prospectPhone, setProspectPhone] = useState('')
  const [contact, setContact] = useState<PickerValue>(null)
  const [assignedTo, setAssignedTo] = useState('')
  const [pipelineType, setPipelineType] = useState<PipelineType | ''>('')
  const [shipTo, setShipTo] = useState('')
  const [needBy, setNeedBy] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()])

  function reset() {
    setCustomer(null)
    setProspectName('')
    setProspectCompany('')
    setProspectEmail('')
    setProspectPhone('')
    setContact(null)
    setAssignedTo('')
    setPipelineType('')
    setShipTo('')
    setNeedBy('')
    setNotes('')
    setLines([emptyLine()])
  }

  // Unified pick: a company sets just the customer; a person sets the contact
  // AND auto-fills their company + prefills ship-to from their Odoo address
  // (only when empty, so we never clobber a typed address).
  async function handlePartyPick(party: Party) {
    setProspectName('')
    setProspectCompany('')
    setProspectEmail('')
    setProspectPhone('')
    if (party.kind === 'company') {
      setCustomer([party.id, party.name])
      setContact(null)
      return
    }
    setContact([party.id, party.name])
    if (party.company_id && party.company_name) {
      setCustomer([party.company_id, party.company_name])
    }
    try {
      const detail = await fetchContactDetail(party.id)
      if (detail.address) setShipTo((cur) => (cur.trim() ? cur : detail.address))
    } catch {
      /* best-effort — ignore lookup failures */
    }
  }

  const partyLabel = contact
    ? customer
      ? `${contact[1]} · ${customer[1]}`
      : contact[1]
    : customer
      ? customer[1]
      : null

  const hasCustomer = !!customer || prospectName.trim().length > 0
  const isProspectPath = !customer && prospectName.trim().length > 0
  const prospectContactValid =
    !isProspectPath || (prospectCompany.trim().length > 0 && prospectEmail.trim().length > 0)
  const hasPipelineType = pipelineType !== ''
  const hasProduct = lines.some((l) => !!l._picked)

  function submit() {
    if (!hasCustomer) {
      toast.danger('Pick a customer or enter a prospect name.')
      return
    }
    if (!prospectContactValid) {
      toast.danger('Prospect orders need a company name and email address.')
      return
    }
    if (!hasPipelineType) {
      toast.danger('Pick a pipeline type.')
      return
    }
    const cleanLines: CreateLineInput[] = lines
      .filter((l) => !!l._picked)
      .map((l) => ({
        matched_product_id: l._picked ? l._picked[0] : undefined,
        matched_product_name: l._picked ? l._picked[1] : undefined,
        final_qty: l.final_qty,
        uom: l.uom || 'ea',
      }))

    create.mutate(
      {
        customer_id: customer ? customer[0] : undefined,
        customer_name: customer ? customer[1] : undefined,
        prospect_name: prospectName.trim() || undefined,
        prospect_company: prospectCompany.trim() || undefined,
        prospect_email: prospectEmail.trim() || undefined,
        prospect_phone: prospectPhone.trim() || undefined,
        contact_id: contact ? contact[0] : undefined,
        contact_name: contact ? contact[1] : undefined,
        assigned_to: assignedTo || undefined,
        pipeline_type: pipelineType as PipelineType,
        ship_to_address: shipTo.trim() || undefined,
        need_by_date: needBy || undefined,
        notes: notes.trim() || undefined,
        lines: cleanLines,
      },
      {
        onSuccess: (order) => {
          toast.success(`Created ${order.order_number}`)
          reset()
          onOpenChange(false)
          onCreated?.(order.id)
        },
        onError: (err) =>
          toast.danger(
            `Create failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      size="wide"
      title="New Sample Order"
      meta="Request SPI's own commercial samples for a customer or prospect."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!hasCustomer || !prospectContactValid || !hasPipelineType || !hasProduct}
            onClick={submit}
          >
            Create order
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Customer or contact</span>
            <CustomerContactPicker
              label={partyLabel}
              onPick={handlePartyPick}
              onClear={() => {
                setCustomer(null)
                setContact(null)
              }}
              placeholder="Search a company or a person…"
            />
            <span className="text-role-metadata">
              Search a company or a person. Picking a person fills in their
              company automatically.
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">…or prospect name</span>
            <Input
              value={prospectName}
              onChange={(e) => {
                setProspectName(e.target.value)
                if (e.target.value.trim()) {
                  setCustomer(null)
                  setContact(null)
                }
              }}
              placeholder="New / unregistered prospect"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              Company name{isProspectPath ? <span className="text-danger-text"> *</span> : null}
            </span>
            <Input
              value={prospectCompany}
              onChange={(e) => setProspectCompany(e.target.value)}
              placeholder="Prospect's company"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              Email address{isProspectPath ? <span className="text-danger-text"> *</span> : null}
            </span>
            <Input
              type="email"
              value={prospectEmail}
              onChange={(e) => setProspectEmail(e.target.value)}
              placeholder="prospect@company.com"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Phone number</span>
            <Input
              type="tel"
              value={prospectPhone}
              onChange={(e) => setProspectPhone(e.target.value)}
              placeholder="(555) 555-5555"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Need by</span>
            <Input type="date" value={needBy} onChange={(e) => setNeedBy(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Owner</span>
            <Select
              value={assignedTo}
              disabled={repsQuery.isLoading}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              <option value="">{repsQuery.isLoading ? 'Loading…' : 'Unassigned'}</option>
              {reps.map((rep) => (
                <option key={rep.email} value={rep.email}>
                  {rep.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              Pipeline type<span className="text-danger-text"> *</span>
            </span>
            <Select
              value={pipelineType}
              onChange={(e) => setPipelineType(e.target.value as PipelineType)}
            >
              <option value="" disabled>
                Select…
              </option>
              {PIPELINE_TYPES.map((pt) => (
                <option key={pt} value={pt}>
                  {PIPELINE_TYPE_LABELS[pt]}
                </option>
              ))}
            </Select>
          </label>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Ship-to address</span>
          <Textarea
            value={shipTo}
            onChange={(e) => setShipTo(e.target.value)}
            rows={2}
            placeholder="Where should the sample ship?"
          />
        </label>

        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-role-section-label">Requested products</span>
            <Button variant="secondary" size="sm" onClick={() => setLines((l) => [...l, emptyLine()])}>
              <Plus size={14} className="mr-1.5" /> Add item
            </Button>
          </div>
          <div className="flex flex-col gap-2">
            {lines.map((line, idx) => (
              <div key={line._key} className="flex items-end gap-2">
                <label className="flex flex-1 flex-col gap-1 text-sm">
                  {idx === 0 ? <span className="text-role-metadata">Product</span> : null}
                  <ProductPicker
                    value={line._picked}
                    searchEndpoint="/api/sample-orders/search/products"
                    limit={30}
                    width={520}
                    placeholder="Search by name, code, INCI or supplier…"
                    onChange={(picked) =>
                      setLines((ls) =>
                        ls.map((l) => (l._key === line._key ? { ...l, _picked: picked } : l)),
                      )
                    }
                  />
                </label>
                <label className="flex w-20 flex-col gap-1 text-sm">
                  {idx === 0 ? <span className="text-role-metadata">Qty</span> : null}
                  <Input
                    type="number"
                    min="0"
                    value={line.final_qty ?? ''}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((l) =>
                          l._key === line._key
                            ? { ...l, final_qty: e.target.value === '' ? undefined : Number(e.target.value) }
                            : l,
                        ),
                      )
                    }
                  />
                </label>
                <label className="flex w-16 flex-col gap-1 text-sm">
                  {idx === 0 ? <span className="text-role-metadata">UOM</span> : null}
                  <Input value={line.uom ?? 'ea'} readOnly tabIndex={-1} />
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Remove item"
                  disabled={lines.length === 1}
                  onClick={() => setLines((ls) => ls.filter((l) => l._key !== line._key))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        </section>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Notes</span>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything the team should know"
          />
        </label>
      </div>
    </Drawer>
  )
}
